import React, { useEffect, useState, useMemo, useCallback } from "react";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import { getStudentsByClassroom, getSubjects, getGradesBySubjectBatch } from "../../api/sponteAPI";
import { normalizeStudentsByClassroom, normalizeSubjects } from "../../utils/sponteDataNormalizer";
import { GradeItem } from "../../components/GradeItem/GradeItem";
import {
  getGradesByClassroom,
  updateAllGrades,
  syncPendingGrades,
  cleanupLocalStorage,
} from "../../api/autenticoAPI";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import BoletimCombinadoPDF from "../../components/BoletimCombinadoPDF";
import AlunosRecuperacaoPDF from "../../components/AlunosRecuperacaoPDF";
import { TransferStudentDialog } from "../../components/grades/TransferStudentDialog";
import { Container, ContentArea, TitleArea, PageTitle, SelectionArea, GradeItemsContainer } from "./styles";
import SelectSys from "../../components/Systech/SelectSys/SelectSys";
import { GradesFilters } from "../../components/grades/GradesFilters";
import { useGrades } from "../../hooks/useGrades";
import {
  clearSyncDataForPeriodChange,
  clearAllGradeSyncKeys,
} from "../../utils/gradesSyncStorage";
import { SponteSection } from "./SponteSection";
import { QuestionnaireClassroomPanel } from "../../components/questionnaires/QuestionnaireClassroomPanel";
import {
  sendFinalGradesToSponte,
  updateAllVisibilityForClass,
} from "../../services/gradesSponteService";
import { Box, Button } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

/** Tempo antes de liberar "Reenviar falhas" após falhas (evita novo erro ao clicar na hora). */
const FINAL_GRADES_RETRY_COOLDOWN_MS = 60000;

const Grades = () => {
  const [anoLetivo, setAnoLetivo] = useState(2026);
  const { user } = React.useContext(AuthContext);
  const [classrooms, setClassrooms] = useState([]);
  const [period, setPeriod] = useState();
  const [subject, setSubject] = useState();
  const [subjects, setSubjects] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState([]);
  const [classGrades, setClassGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  // Flag para indicar que classGrades foi atualizado diretamente (não via hook)
  const classGradesUpdatedDirectlyRef = React.useRef(false);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("error");
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [updateStudentVisibility, setUpdateStudentVisibility] = useState(0);
  const [showMessage, setShowMessage] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [validate, setValidate] = useState(false);
  const [validatingGrades, setValidatingGrades] = useState(false);
  // Dados do Sponte obtidos via batch para passar para os GradeItem
  const [sponteGradesData, setSponteGradesData] = useState({});
  
  // Estado para o modal do Talento Autêntico
  const [talentoAutenticoOpen, setTalentoAutenticoOpen] = useState(false);
  const [transferStudentOpen, setTransferStudentOpen] = useState(false);

  // IDs dos alunos cujo envio ao Sponte falhou na última operação (para reenvio parcial)
  const [finalGradesFailedIds, setFinalGradesFailedIds] = useState([]);
  /** Timestamp (ms) em que o botão "Reenviar falhas" pode ser clicado */
  const [retryFailuresCooldownUntil, setRetryFailuresCooldownUntil] = useState(null);
  const [, setCooldownUiTick] = useState(0);

  useEffect(() => {
    if (!retryFailuresCooldownUntil || Date.now() >= retryFailuresCooldownUntil) {
      if (
        retryFailuresCooldownUntil &&
        Date.now() >= retryFailuresCooldownUntil
      ) {
        setRetryFailuresCooldownUntil(null);
      }
      return undefined;
    }
    const id = setInterval(() => {
      setCooldownUiTick((x) => x + 1);
      if (Date.now() >= retryFailuresCooldownUntil) {
        setRetryFailuresCooldownUntil(null);
      }
    }, 400);
    return () => clearInterval(id);
  }, [retryFailuresCooldownUntil]);

  const retryFailuresCooldownSecondsLeft =
    retryFailuresCooldownUntil &&
    Date.now() < retryFailuresCooldownUntil
      ? Math.max(
          0,
          Math.ceil((retryFailuresCooldownUntil - Date.now()) / 1000)
        )
      : 0;

  // Listener global para erros 429 de qualquer requisição
  useEffect(() => {
    const handleRateLimit = (event) => {
      const { message: errorMessage } = event.detail;
      
      // Exibir mensagem
      setShow(false);
      setTimeout(() => {
        setMessage(errorMessage);
        setType("error");
        setShow(true);
      }, 50);
    };

    window.addEventListener('rateLimitExceeded', handleRateLimit);
    
    return () => {
      window.removeEventListener('rateLimitExceeded', handleRateLimit);
    };
  }, []);

  // Extrair TurmaID de forma consistente
  const turmaId = useMemo(() => {
    if (!classrooms || classrooms.length < 2) return undefined;
    let id = classrooms[1];
    if (Array.isArray(id)) {
      id = id[0];
    }
    id = typeof id === 'string' ? parseInt(id, 10) : Number(id);
    return isNaN(id) ? undefined : id;
  }, [classrooms]);

  // Botão visível assim que há turma (não espera lista de alunos do Sponte — isso só roda ao abrir o modal).
  const canOfferClassTransfer = useMemo(() => {
    const lvl = String(user?.level || "").toLowerCase();
    if (lvl === "teacher" || !turmaId) return false;
    return true;
  }, [user?.level, turmaId]);

  // Filtros para o hook de notas (useGrades)
  const gradeFilters = useMemo(() => {
    const filters = {
      TurmaID: turmaId,
      DisciplinaID: subject && subject.id ? Number(subject.id) : undefined,
      period: period ? String(period) : period, // Garantir que period seja string para compatibilidade com backend
    };
    return filters;
  }, [turmaId, subject?.id, period]);

  useEffect(() => {
    setFinalGradesFailedIds([]);
    setRetryFailuresCooldownUntil(null);
  }, [period, subject?.id, turmaId]);

  const { grades: hookGrades, loading: gradesLoading, refetch: refetchGrades } = useGrades(gradeFilters);

  const refreshOriginClassStudentsAndGrades = useCallback(async () => {
    if (!turmaId) return;
    try {
      const res = await getStudentsByClassroom(turmaId);
      setStudentsByClass(normalizeStudentsByClassroom(res));
      await refetchGrades();
    } catch (e) {
      console.error("[Grades] Pós-transferência: falha ao atualizar lista", e);
    }
  }, [turmaId, refetchGrades]);
  
  const sendFinalGrades = useCallback(
    async (options = {}) => {
      const { retryFailedOnly = false } = options;

      const grades = classGrades?.grades;
      if (!Array.isArray(grades) || grades.length === 0) {
        setType("warning");
        setMessage("Nenhuma nota carregada para envio.");
        setShow(true);
        return;
      }

      let gradesSubset = null;
      if (retryFailedOnly) {
        if (finalGradesFailedIds.length === 0) {
          setType("warning");
          setMessage(
            "Não há falhas pendentes para reenviar. Use Calcular notas finais primeiro."
          );
          setShow(true);
          return;
        }
        const idSet = new Set(finalGradesFailedIds.map((id) => String(id)));
        gradesSubset = grades.filter((g) => idSet.has(String(g.AlunoID)));
        if (gradesSubset.length === 0) {
          setFinalGradesFailedIds([]);
          setRetryFailuresCooldownUntil(null);
          setType("warning");
          setMessage(
            "Os alunos com falha não estão mais nesta lista. Recarregue as notas ou envie a turma inteira novamente."
          );
          setShow(true);
          return;
        }
      } else {
        setFinalGradesFailedIds([]);
        setRetryFailuresCooldownUntil(null);
      }

      const result = await sendFinalGradesToSponte({
        classGrades,
        gradesSubset: retryFailedOnly ? gradesSubset : null,
        setLoading,
        setMessage,
        setType,
        setShow,
      });

      const failed = Array.from(
        new Set((result.failedGrades || []).map((g) => g.AlunoID))
      );
      setFinalGradesFailedIds(failed);
      if (failed.length > 0) {
        setRetryFailuresCooldownUntil(
          Date.now() + FINAL_GRADES_RETRY_COOLDOWN_MS
        );
      } else {
        setRetryFailuresCooldownUntil(null);
      }
    },
    [classGrades, finalGradesFailedIds]
  );

  const retryFailedFinalGrades = useCallback(() => {
    sendFinalGrades({ retryFailedOnly: true });
  }, [sendFinalGrades]);

  // Função interna para executar a verificação de médias
  const executeValidation = useCallback(async () => {
    if (!turmaId || !subject?.id || !period) {
      return;
    }

    const normalizedStudents = normalizeStudentsByClassroom(studentsByClass);
    if (normalizedStudents.length === 0) {
      return;
    }

    try {
      setValidatingGrades(true);
      setMessage(`Verificando médias de ${normalizedStudents.length} alunos...`);
      setType("info");
      setShow(true);

      // Extrair IDs dos alunos
      const alunoIDs = normalizedStudents.map(s => s.AlunoID || s.AlunoId).filter(Boolean);
      
      // Fazer requisição batch (sempre buscar dados atualizados)
      const result = await getGradesBySubjectBatch(alunoIDs, turmaId, subject.id);

      // Passar os dados do batch para os GradeItem
      // result.data é um objeto onde a chave é o AlunoID e o valor são as notas do Sponte
      setSponteGradesData(result.data || {});
      // Ativar modo de validação para que os componentes GradeItem comparem as notas
      setValidate(true);
      
      // Aguardar um tempo suficiente para garantir que todos os componentes processem os dados
      // Aumentado para 5 segundos para dar tempo suficiente de processamento e exibição
      setTimeout(() => {
        setValidate(false);
        // IMPORTANTE: NÃO limpar sponteGradesData aqui - manter os dados para que a divergência continue visível
        // Os dados só serão limpos quando mudar período/disciplina ou quando validar novamente
        // A exibição agora depende de gradeSponte (que persiste) e não apenas de validate
      }, 5000);

      if (result.stats.failed > 0) {
        setMessage(`Verificação concluída: ${result.stats.success} sucesso, ${result.stats.failed} falhas`);
        setType("warning");
      } else {
        setMessage(`Todas as ${result.stats.success} médias foram verificadas com sucesso`);
        setType("success");
      }
      setShow(true);
      setValidatingGrades(false); // Reabilitar botões após verificação bem-sucedida
    } catch (error) {
      // Tratamento específico para erro 429 (Rate Limit / Bloqueio)
      const isRateLimit = error.response?.status === 429 || 
                         error.rateLimitExceeded || 
                         error.response?.data?.rateLimitExceeded ||
                         (error.message && error.message.includes('API bloqueada'));
      
      if (isRateLimit) {
        const responseData = error.response?.data || {};
        const isLocalThrottle = responseData.source === 'local';

        // Throttle interno: sem toast (overlay escuro também não aparece no client)
        if (isLocalThrottle) {
          setShow(false);
          setValidatingGrades(false);
          return;
        }

        const waitTime = responseData.waitTime || error.waitTime || 120;
        const minutes = Math.ceil(waitTime / 60);
        const seconds = waitTime % 60;
        const timeMessage = minutes > 1 
          ? `${minutes} minutos e ${seconds} segundos`
          : `${waitTime} segundos`;

        const errorMessage = `⚠️ API Sponte bloqueada!\n\n${responseData.message || 'O limite de requisições foi atingido na API Sponte.'}\n\n⏱️ Aguarde ${timeMessage} antes de tentar novamente.\n\nPor favor, não continue tentando enquanto o bloqueio estiver ativo.`;
        
        // Garantir que a mensagem seja exibida - fechar qualquer mensagem anterior primeiro
        setShow(false);
        
        // Usar setTimeout para garantir que o estado seja atualizado corretamente
        setTimeout(() => {
          setMessage(errorMessage);
          setType("error");
          setShow(true);
        }, 50);
        
        setValidatingGrades(false);
        return;
      }
      
      // Outros erros
      console.error("Erro ao verificar médias:", error);
      setMessage(`Erro ao verificar médias: ${error.message || 'Erro desconhecido'}`);
      setType("error");
      setShow(true);
      setValidatingGrades(false);
    }
  }, [turmaId, subject?.id, period, studentsByClass, setMessage, setType, setShow]);

  // Função para executar verificação de médias (não é mais toggle)
  const handleValidateAverages = useCallback(() => {
    // Sempre executar verificação quando o botão for clicado
    executeValidation();
  }, [executeValidation]);

  useEffect(() => {
    if (!classrooms || classrooms.length === 0) {
      setStudentsByClass([]);
      setSubjects([]);
      setSubject(undefined);
      setClassGrades({ grades: [] });
      return;
    }

    // Extrair TurmaID corretamente - pode ser um array ou número
    let turmaId = classrooms[1];
    if (Array.isArray(turmaId)) {
      turmaId = turmaId[0]; // Pegar o primeiro elemento do array
    }
    turmaId = typeof turmaId === 'string' ? parseInt(turmaId, 10) : Number(turmaId);

    if (!turmaId || isNaN(turmaId)) {
      setStudentsByClass([]);
      setSubjects([]);
      return;
    }

    Promise.all([
      getSubjects(turmaId)
        .then((res) => {
          const normalized = normalizeSubjects(res);
          setSubjects(normalized);
          return normalized;
        })
        .catch(err => {
          setSubjects([]);
          return [];
        }),
      getStudentsByClassroom(turmaId)
        .then((res) => {
          const normalized = normalizeStudentsByClassroom(res);
          setStudentsByClass(normalized);
          return normalized;
        })
        .catch(err => {
          setStudentsByClass([]);
          return [];
        })
    ]);

    setSubject(undefined);
    setClassGrades({ grades: [] });
  }, [classrooms]);

  useEffect(() => {
    setSelectAll(false);

    if (period !== undefined && subject !== undefined) {
      // Limpar dados de sincronização expirados e de outros períodos
      clearSyncDataForPeriodChange(period);
      // Limpar notas antigas quando mudar período ou disciplina
      setClassGrades({ grades: [] });
    }

    setValidate(false); // Resetar o estado de validação
    setSponteGradesData({}); // Limpar dados do Sponte ao mudar período/disciplina

    setUpdateStudentVisibility(0);
    // Não setar loading aqui - deixar o hook useGrades gerenciar
  }, [classrooms, subject, period]);

  // Sincronizar o resultado do hook useGrades com o estado local usado pela tela
  useEffect(() => {
    // Se classGrades foi atualizado diretamente (ex: após atualizar visibilidade),
    // não sobrescrever com dados do hook até que o hook seja atualizado com os dados corretos
    if (classGradesUpdatedDirectlyRef.current) {
      // Resetar a flag após um pequeno delay para permitir que o hook seja atualizado
      setTimeout(() => {
        classGradesUpdatedDirectlyRef.current = false;
      }, 1000);
      return;
    }
    // CRÍTICO: Filtrar notas apenas do período atual para evitar dados de períodos anteriores
    const currentPeriodString = period ? String(period) : period;
    const filteredGrades = Array.isArray(hookGrades) 
      ? hookGrades.filter(grade => {
          const gradePeriod = grade?.period ? String(grade.period) : null;
          return gradePeriod === currentPeriodString;
        })
      : [];
    
    // Usar JSON.stringify para comparar se os dados realmente mudaram
    // Isso evita atualizações desnecessárias quando a referência muda mas os dados são os mesmos
    setClassGrades((prev) => {
      const prevGradesStr = JSON.stringify(prev?.grades || []);
      const newGradesStr = JSON.stringify(filteredGrades || []);
      
      // Só atualizar se os dados realmente mudaram
      if (prevGradesStr !== newGradesStr) {
        return { grades: filteredGrades };
      }
      return prev;
    });
    
    // Atualizar loading apenas se realmente mudou
    setLoading((prev) => {
      return prev !== gradesLoading ? gradesLoading : prev;
    });
  }, [hookGrades, gradesLoading, period, subject?.id, turmaId]);

  useEffect(() => {
    const setInitialStates = () => {
      // Limpar todas as seleções quando o ano letivo mudar
      setClassrooms([]);
      setClassGrades([]);
      setPeriod(undefined);
      setSubject(undefined);
      setSubjects([]);
      setStudentsByClass([]);
    };
    setInitialStates();
  }, [anoLetivo]);

  const repeatGrades = async (value, currentSetter) => {
    if (!turmaId || !subject) return;
    const normalizedStudents = normalizeStudentsByClassroom(studentsByClass);
    let students = normalizedStudents.map((res) => res.AlunoID);
    const values = {
      [currentSetter]: value,
    };
    setLoading(true);
    const promises = students.map((item) =>
      updateAllGrades(item, turmaId, subject.id, period, values)
    );
    try {
      await Promise.all(promises);
      const updatedGrades = await getGradesByClassroom(
        turmaId,
        subject.id,
        period
      );
      // Usar setClassGrades diretamente aqui também, mas não precisa da flag
      // pois isso é uma atualização normal de notas, não após visibilidade
      setClassGrades(updatedGrades);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao atualizar notas:", error);
    } finally {
      setTimeout(() => {
        const inputFields = document.querySelectorAll(".grade-input");
        inputFields.forEach((input) => input.focus());
        const inputFinish = document.querySelector(".finish");
        inputFinish.focus();
      }, 1000);
    }
  };

  // Wrapper para setClassGrades que define a flag quando atualizado diretamente
  const setClassGradesDirectly = React.useCallback((grades) => {
    classGradesUpdatedDirectlyRef.current = true;
    setClassGrades(grades);
  }, []);

  // Função para atualizar visibilidade quando o checkbox "selecionar todos" muda
  const handleSelectAllChange = React.useCallback(async (newSelectAllValue) => {
    // Validar se temos os dados necessários
    if (!period || !subject || !studentsByClass || studentsByClass.length === 0) {
      return;
    }
    
    // Atualizar o estado local primeiro
    setSelectAll(newSelectAllValue);
    
    // Salvar automaticamente no backend
    await updateAllVisibilityForClass({
      period,
      subject,
      studentsByClass,
      classrooms,
      selectAll: newSelectAllValue, // Usar o novo valor
      setLoading,
      setType,
      setMessage,
      setInfoMessage,
      setShow,
      setShowMessage,
      setClassGrades: setClassGradesDirectly, // Usar wrapper que define a flag
      setUpdateStudentVisibility,
      refetchGrades, // Passar a função de refetch do hook
    });
  }, [period, subject, studentsByClass, classrooms, setClassGradesDirectly, refetchGrades]);

  // Manter a função antiga por compatibilidade (caso ainda seja usada em algum lugar)
  const updateAllVisibility = async () => {
    await updateAllVisibilityForClass({
      period,
      subject,
      studentsByClass,
      classrooms,
      selectAll,
      setLoading,
      setType,
      setMessage,
      setInfoMessage,
      setShow,
      setShowMessage,
      setClassGrades: setClassGradesDirectly, // Usar wrapper que define a flag
      setUpdateStudentVisibility,
      refetchGrades, // Passar a função de refetch do hook
    });
  };

  // Função para atualizar apenas o aluno alterado no array de notas
  const handleGradeSaved = (updatedGrade) => {
    if (!updatedGrade || !updatedGrade.AlunoID) return;
    setClassGrades((prev) => {
      if (!prev || !prev.grades) return prev;
      const newGrades = prev.grades.map((grade) =>
        String(grade.AlunoID) === String(updatedGrade.AlunoID)
          ? { ...grade, ...updatedGrade }
          : grade
      );
      return { ...prev, grades: newGrades };
    });
  };

  // Efeito para sincronizar dados pendentes ao inicializar o componente
  useEffect(() => {
    // Limpar dados inconsistentes do localStorage
    cleanupLocalStorage();
    
    // Tentar sincronizar notas pendentes
    syncPendingGrades().then(result => {
      if (result && result.total > 0) {
        setInfoMessage(`Sincronização: ${result.successful} notas recuperadas de ${result.total} pendentes`);
        setShowMessage(true);
      }
    }).catch(error => {
      console.error("Erro ao executar sincronização inicial:", error);
    });
    
    // Carregar período ativo da sessão se houver
    const savedPeriod = sessionStorage.getItem('currentPeriod');
    if (savedPeriod) {
      setPeriod(savedPeriod);
    }
  }, []);

  // Cleanup quando o componente for desmontado
  useEffect(() => {
    return () => {
      // Limpar qualquer estado ou listeners quando sair da página
    };
  }, []); // Array vazio - executar apenas na montagem/desmontagem

  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <TitleArea>
          <SelectSys anoLetivo={anoLetivo} setAnoLetivo={setAnoLetivo} />
          <PageTitle>Sistema de Avaliações</PageTitle>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <BoletimCombinadoPDF
              turmaId={turmaId || null}
              turmaNome={classrooms.length > 0 ? classrooms[0] : ""}
            />
            <AlunosRecuperacaoPDF
              turmaId={turmaId || null}
              turmaNome={classrooms.length > 0 ? classrooms[0] : ""}
              anoLetivo={anoLetivo}
              period={period}
            />
            {canOfferClassTransfer && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SwapHorizIcon />}
                onClick={() => setTransferStudentOpen(true)}
                sx={{
                  height: "40px",
                  textTransform: "none",
                  borderRadius: "4px",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                }}
              >
                Mudança de turma
              </Button>
            )}
          </Box>
        </TitleArea>

        <QuestionnaireClassroomPanel
          turmaId={turmaId}
          classroomName={classrooms.length > 0 ? classrooms[0] : ""}
          userLevel={user?.level}
        />

        <SponteSection
          user={user}
          classrooms={classrooms}
          period={period}
          subject={subject}
          studentsByClass={studentsByClass}
          loading={loading}
          show={show}
          setShow={setShow}
          type={type}
          message={message}
          showMessage={showMessage}
          setShowMessage={setShowMessage}
          infoMessage={infoMessage}
          talentoAutenticoOpen={talentoAutenticoOpen}
          setTalentoAutenticoOpen={setTalentoAutenticoOpen}
          open={open}
          setOpen={setOpen}
          sendFinalGrades={sendFinalGrades}
          updateAllVisibility={updateAllVisibility}
          onValidateAverages={handleValidateAverages}
          validatingGrades={validatingGrades}
          failedFinalSendCount={finalGradesFailedIds.length}
          onRetryFailedFinalGrades={retryFailedFinalGrades}
          retryFailuresCooldownSecondsLeft={retryFailuresCooldownSecondsLeft}
        />

        <SelectionArea>
          <GradesFilters
            anoLetivo={anoLetivo}
            setAnoLetivo={setAnoLetivo}
            classrooms={classrooms}
            setClassrooms={setClassrooms}
            period={period}
            setPeriod={(newPeriod) => {
              // Mantém a mesma lógica de limpeza ao mudar período
              setClassGrades([]);
              setLoading(true);

              clearAllGradeSyncKeys();
              setPeriod(newPeriod);
            }}
            subject={subject}
            setSubject={setSubject}
            subjects={subjects}
          />
        </SelectionArea>

        <GradeItemsContainer>
          {(() => {
            const normalizedStudents = normalizeStudentsByClassroom(studentsByClass);
            const conditions = {
              hasStudents: normalizedStudents.length > 0,
              hasPeriod: period !== undefined,
              hasSubject: subject !== undefined,
              notLoading: !loading,
              hasClassGrades: classGrades && classGrades.grades && Array.isArray(classGrades.grades),
            };
            
            const shouldRender = conditions.hasStudents &&
              conditions.hasPeriod &&
              conditions.hasSubject &&
              conditions.notLoading &&
              conditions.hasClassGrades;
            return shouldRender &&
              normalizedStudents.map((student, index) => (
                <GradeItem
                  key={student.AlunoID}
                  aluno={{
                    nome: `${student.NumeroChamada} - (${student.AlunoID}) ${student.Nome}`,
                    AlunoID: student.AlunoID,
                  }}
                  legend={index === 0}
                  tabIndex={index + 13}
                  last={normalizedStudents.length}
                  TurmaID={turmaId}
                  DisciplinaID={subject.id}
                  nomeDisciplina={subject.name}
                  period={period ? String(period) : period} // Garantir que period seja string para compatibilidade
                  classGrades={classGrades}
                  selectAll={selectAll}
                  setSelectAll={setSelectAll}
                  onSelectAllChange={handleSelectAllChange} // Passar função para salvar automaticamente
                  updateStudentVisibility={updateStudentVisibility}
                  repeatGrades={repeatGrades}
                  validate={validate}
                  setValidate={setValidate}
                  onGradeSaved={handleGradeSaved}
                  sponteGradesData={sponteGradesData} // Passar dados do batch do Sponte
                />
              ));
          })()}
        </GradeItemsContainer>
      </ContentArea>
      <TransferStudentDialog
        open={transferStudentOpen}
        onClose={() => setTransferStudentOpen(false)}
        anoLetivo={anoLetivo}
        turmaOrigemId={turmaId}
        turmaOrigemNome={classrooms.length > 0 ? classrooms[0] : ""}
        unit={user?.unit}
        onTransferred={refreshOriginClassStudentsAndGrades}
      />
    </Container>
  );
};

export default Grades;
